/**
 * Sign-out sequence (FN-5).
 *
 * The old handler raced `cloudSave().catch(() => {})` against a 5s timer and
 * then cleared IndexedDB unconditionally. Three separate ways to lose work:
 * the rejection was discarded, a slow-but-successful save was indistinguishable
 * from a failed one, and an unnamed project was wiped with no save attempted at
 * all. Offline, every sign-out destroyed the session.
 *
 * The rule now: **the local copy is deleted only on positive proof of cloud
 * durability**, which is `core/durability.js`'s job, and never on a timeout, a
 * failure, or a dirty flag. Everything else keeps the copy — it is
 * account-scoped and ownership-stamped, so keeping it costs nothing and is the
 * only recoverable choice.
 *
 * Ordering is load-bearing and deliberately different from the old flow:
 * collaboration is disconnected AFTER the user decides, so Cancel returns them
 * to a live session rather than a silently dead one.
 *
 * Every dependency is injected. This module imports only the pure verdict
 * helpers, so the whole decision matrix is testable in node with no DOM, no
 * IndexedDB and no network.
 */

import { evaluateDurability, isSafeToDiscardLocalCopy, DURABILITY } from './durability.js';

/** Maps a durability verdict + save attempt onto the dialog's reason copy. */
function dialogReasonFor({ verdict, saveAttempt, needsName }) {
  if (needsName) return 'unnamed';
  if (saveAttempt === 'failed') return 'save-failed';
  if (saveAttempt === 'timeout') return 'save-timeout';
  if (verdict?.reason === 'never-in-cloud') return 'never-in-cloud';
  return 'unknown';
}

const withTimeout = (promise, ms, delay) =>
  Promise.race([
    promise.then(
      () => ({ outcome: 'ok' }),
      (error) => ({ outcome: 'failed', error })
    ),
    delay(ms).then(() => ({ outcome: 'timeout' }))
  ]);

/** Module-scope single flight — a second Sign out click joins the first. */
let _inFlight = null;

/**
 * @param {Object} deps  — everything injected; see the call site in shell.js
 * @returns {Promise<{done: boolean, cancelled?: boolean, cleared?: boolean, reason?: string}>}
 */
export function runLogoutSequence(deps) {
  if (_inFlight) return _inFlight;
  _inFlight = _run(deps).finally(() => {
    _inFlight = null;
  });
  return _inFlight;
}

async function _run({
  store,
  isRealAccount = false,
  projectNeedsName = () => false,
  cloudSave,
  getCloudSyncRecord = () => null,
  confirmUnsaved,
  downloadBackup,
  disconnectCollab = async () => {},
  postSignedOut = () => {},
  logoutAccount = async () => {},
  reload = () => {},
  reportError = () => {},
  delay = (ms) => new Promise((r) => setTimeout(r, ms)),
  cloudSaveTimeoutMs = 5000
}) {
  const principal = store.getPrincipalScope?.() ?? null;
  const readVerdict = (extra = {}) => {
    const state = store.getState();
    return evaluateDurability({
      state,
      principal,
      sync: state?.projectId ? getCloudSyncRecord(state.projectId) : null,
      ...extra
    });
  };

  let verdict = readVerdict();
  let saveAttempt = 'none';
  const needsName = projectNeedsName(store.getState());
  // The verdict is evidence ABOUT ONE PROJECT AT ONE REVISION. Everything after
  // this point can await — a save, a dialog, a collab disconnect — and the user
  // can keep editing throughout. Binding both here is what lets the deletion
  // boundary below prove nothing moved, rather than assume it.
  const verdictProjectId = store.getState()?.projectId ?? null;
  let revisionAtVerdict = store.getRevision?.() ?? null;

  // One bounded attempt to make the work durable before asking anything. An
  // unnamed project is skipped because the save would fail on a missing name,
  // not because its work matters less — it goes straight to the dialog.
  if (!isSafeToDiscardLocalCopy(verdict) && isRealAccount && !needsName) {
    const revisionAtSaveStart = store.getRevision?.() ?? null;
    const result = await withTimeout(
      Promise.resolve().then(() => cloudSave()),
      cloudSaveTimeoutMs,
      delay
    );
    saveAttempt = result.outcome;
    if (result.outcome === 'failed') {
      // Not silent: the dialog explains the consequence, and the reporter keeps
      // the actual Error rather than discarding it the way the old catch did.
      reportError('Sign out — cloud save', result.error, { user: 'silent' });
    }
    // Re-read AFTER the attempt, and pass the revision either side of it: a
    // save that succeeded against a state the user has since edited has not
    // made the current content durable.
    verdict = readVerdict({ revisionAtSaveStart, revisionNow: store.getRevision?.() ?? null });
    // The span the deletion boundary must prove is "from THIS verdict to the
    // delete", so it restarts here rather than at the top of the function.
    revisionAtVerdict = store.getRevision?.() ?? null;
  }

  // The timed-out save may still be in flight and may still succeed. That is
  // fine and deliberately not awaited: a late success cannot delete anything,
  // because the only deletion below is gated on the verdict already read.
  if (!isSafeToDiscardLocalCopy(verdict)) {
    const choice = await confirmUnsaved({
      reason: dialogReasonFor({ verdict, saveAttempt, needsName }),
      projectName: store.getState()?.projectName || '',
      onDownload: downloadBackup
    });
    if (choice !== 'signout') {
      // Nothing has been torn down at this point — no collab disconnect, no
      // flush, no clear, no cookie. Returning to editing is genuinely a return.
      return { done: false, cancelled: true, reason: verdict.reason };
    }
  }

  try {
    await disconnectCollab({ flush: true, capMs: 2000 });
  } catch (err) {
    reportError('Sign out — collaboration', err, { user: 'silent' });
  }

  // Flush ALWAYS: settling the pending autosave is what preserves unsynced work
  // for this account, and it is also the last write before a synced project's
  // slot is removed.
  try {
    await store.flushAutoSave();
  } catch (err) {
    reportError('Sign out — autosave flush', err, { user: 'silent' });
  }

  // ── The point of no return ────────────────────────────────────────────────
  //
  // Everything above this line can await, and the user can keep typing through
  // all of it. Two separate defects lived here:
  //
  //   1. The verdict was read BEFORE the disconnect and reused after it, so an
  //      edit made during teardown was flushed to the local slot and then
  //      deleted on the strength of evidence that predated it.
  //   2. `flushAutoSave()` RESOLVES on a terminal write failure — state.js
  //      reports the error on the bus and resolves anyway — so a resolved
  //      promise was being treated as proof that a copy exists.
  //
  // Both are answered the same way: prove it now, by reading it back, and treat
  // any disagreement as a refusal.
  const liveNow = store.getState();
  const loadedNow = liveNow?.projectId ?? null;
  const revisionNow = store.getRevision?.() ?? null;
  const freshVerdict = readVerdict();
  const unchanged =
    loadedNow === verdictProjectId &&
    revisionNow === revisionAtVerdict &&
    freshVerdict.status === verdict.status;

  /** Did the slot actually receive THIS content? A read-back, never a promise. */
  const persisted = await (async () => {
    try {
      const record = await store.loadFromIDB({ projectId: loadedNow, legacyFallback: false });
      if (!record?.state) return { ok: false, reason: 'no-record' };
      if (record.state.projectId !== loadedNow) return { ok: false, reason: 'other-project' };
      if (Number(record.state.updatedAt) !== Number(liveNow?.updatedAt)) {
        return { ok: false, reason: 'stale-record' };
      }
      return { ok: true };
    } catch (err) {
      reportError('Sign out — verify local copy', err, { user: 'silent' });
      return { ok: false, reason: 'unreadable' };
    }
  })();

  let cleared = false;
  if (isSafeToDiscardLocalCopy(freshVerdict) && unchanged) {
    try {
      // Addressed explicitly rather than relying on "whatever is loaded".
      await store.clearIDB({ projectId: verdictProjectId });
      cleared = true;
    } catch (err) {
      reportError('Sign out — clear local copy', err, { user: 'silent' });
    }
  } else if (isSafeToDiscardLocalCopy(verdict)) {
    // Held durable when we asked, not now. Keep the copy and say which fact
    // moved — a silent retention reads identical to a bug.
    reportError('Sign out — durability evidence expired', new Error('evidence-expired'), {
      user: 'silent',
      detail:
        `project ${verdictProjectId} → ${loadedNow}; revision ${revisionAtVerdict} → ${revisionNow}; ` +
        `verdict ${verdict.status} → ${freshVerdict.status}`
    });
  } else if (!persisted.ok) {
    // The work is unsynced AND the local copy does not hold it. Reloading here
    // destroys the only version that exists. Stop, and leave the app editable.
    reportError('Sign out', new Error(`local-copy-not-written:${persisted.reason}`), {
      user: 'toast',
      message:
        "Your latest changes could not be saved on this device, so you're still signed in. " +
        'Download a backup before signing out.'
    });
    return {
      done: false,
      persistFailed: true,
      reason: persisted.reason,
      durability: freshVerdict.status
    };
  }

  // SB-3b: tell this account's other tabs BEFORE the cookie is cleared —
  // afterwards the principal is gone and the channel has nothing to scope to.
  postSignedOut();
  try {
    await logoutAccount();
  } catch (err) {
    // The server may already be 401; the reload below completes sign-out either
    // way, which is why this is silent rather than blocking.
    reportError('Sign out — server', err, { user: 'silent' });
  }
  reload();
  return { done: true, cleared, reason: verdict.reason, durability: verdict.status };
}

export { DURABILITY };

/** Test seam — the single-flight latch is module state. */
export function _resetLogoutFlowForTesting() {
  _inFlight = null;
}
